import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Icons from '../components/Icons';
import BulkActionBar from '../components/inventory/BulkActionBar';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import StaffService from '../services/StaffService';

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};

export default function StaffPage() {
  const { students, incidents, inventoryItems, inventoryController, controller } = useStudentStore();
  const navigate = useNavigate();
  const location = useLocation();
  const staffService = useMemo(() => new StaffService(), []);
  const [staffList, setStaffList] = useState(() => safeList(staffService));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeKpi, setActiveKpi] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeStaffId, setActiveStaffId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignStudents, setAssignStudents] = useState([]);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState('');
  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    setStaffList(safeList(staffService));
  }, [staffService]);

  useEffect(() => {
    const nextMessage = location.state?.savedMessage;
    if (!nextMessage) return;
    setFlashMessage(nextMessage);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  const visibleStaff = useMemo(() => staffList.filter((staff) => !staff.systemUser), [staffList]);
  const studentAssignments = useMemo(() => {
    const map = new Map();
    visibleStaff.forEach((staff) => map.set(staff.id, []));
    students.forEach((student) => {
      const keyWorkerId = student.keyWorker?.id;
      if (keyWorkerId && map.has(keyWorkerId)) {
        map.get(keyWorkerId).push(student);
      }
      (student.supportWorkers || []).forEach((worker) => {
        if (worker.id && map.has(worker.id)) {
          map.get(worker.id).push(student);
        }
      });
    });
    return map;
  }, [visibleStaff, students]);

  const complianceAlerts = useMemo(() => {
    const now = new Date();
    const soon = new Date();
    soon.setDate(now.getDate() + 30);
    return visibleStaff.reduce((acc, staff) => {
      const docs = staff.certificates?.length ? staff.certificates : staff.documents || [];
      const count = docs.filter((doc) => {
        const expiry = parseDateValue(doc.expiryDate || doc.expiry);
        return expiry && expiry <= soon;
      }).length;
      acc.set(staff.id, count);
      return acc;
    }, new Map());
  }, [visibleStaff]);

  const staffRows = useMemo(() => {
    return visibleStaff
      .filter((staff) => {
        if (!searchQuery) return true;
        return `${staff.name} ${staff.role}`.toLowerCase().includes(searchQuery.toLowerCase());
      })
      .map((staff) => {
        const assignedStudents = studentAssignments.get(staff.id) || [];
        const openIncidents = incidents.filter(
          (incident) => incident.assignedKeyWorker === staff.name && incident.status === 'open'
        ).length;
        const overdueFollowUps = incidents.filter((incident) => {
          if (incident.assignedKeyWorker !== staff.name) return false;
          if (!incident.followUpDate) return false;
          const followUp = parseDateValue(incident.followUpDate);
          return followUp && followUp < new Date();
        }).length;
        const overdueInventory = inventoryItems.filter((item) => {
          const assignment = inventoryController.getActiveAssignment(item);
          if (!assignment || assignment.assignedToType !== 'staff') return false;
          if (assignment.assignedToId !== staff.id) return false;
          return inventoryController.getDueStatus(item).isOverdue;
        }).length;
        const compliance = complianceAlerts.get(staff.id) || 0;
        const overdueTasks = overdueFollowUps + overdueInventory + compliance;
        return {
          ...staff,
          assignedStudents,
          openIncidents,
          overdueTasks,
          compliance
        };
      })
      .sort((a, b) => b.overdueTasks - a.overdueTasks || b.assignedStudents.length - a.assignedStudents.length);
  }, [visibleStaff, searchQuery, incidents, inventoryItems, inventoryController, studentAssignments, complianceAlerts]);

  const kpiItems = useMemo(() => {
    const total = staffRows.length;
    const activeCaseload = staffRows.filter((staff) => staff.assignedStudents.length > 0).length;
    const overdueActions = staffRows.filter((staff) => staff.overdueTasks > 0).length;
    const complianceIssues = staffRows.filter((staff) => staff.compliance > 0).length;
    return [
      { id: 'total', label: 'Total staff', value: total },
      { id: 'caseload', label: 'Active caseload', value: activeCaseload },
      { id: 'overdue', label: 'Overdue actions', value: overdueActions, badge: { label: 'Action', variant: 'danger' } },
      { id: 'compliance', label: 'Compliance issues', value: complianceIssues }
    ];
  }, [staffRows]);

  const filteredRows = useMemo(() => {
    if (!activeKpi) return staffRows;
    if (activeKpi === 'overdue') return staffRows.filter((row) => row.overdueTasks > 0);
    if (activeKpi === 'caseload') return staffRows.filter((row) => row.assignedStudents.length > 0);
    if (activeKpi === 'compliance') return staffRows.filter((row) => row.compliance > 0);
    return staffRows;
  }, [activeKpi, staffRows]);

  const activeStaff = staffRows.find((staff) => staff.id === activeStaffId);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (value) => {
    if (!value) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredRows.map((row) => row.id)));
  };

  const openDrawer = (id) => {
    setActiveStaffId(id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setActiveStaffId(null);
    setDrawerOpen(false);
  };

  const bulkActions = useMemo(() => {
    return [
      {
        label: 'Assign students',
        onClick: () => setAssignModalOpen(true),
        disabled: selectedIds.size !== 1
      },
      {
        label: 'Reassign workload',
        onClick: () => setReassignModalOpen(true),
        disabled: selectedIds.size === 0
      },
      {
        label: 'Export',
        onClick: () => {
          const rows = staffRows
            .filter((row) => selectedIds.has(row.id))
            .map((row) => [row.name, row.role, row.assignedStudents.length, row.openIncidents]);
          const csv = ['Name,Role,Active Students,Open Incidents', ...rows.map((row) => row.join(','))].join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'staff_export.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        disabled: selectedIds.size === 0
      }
    ];
  }, [selectedIds, staffRows]);

  const applyAssignStudents = () => {
    const targetId = Array.from(selectedIds)[0];
    const staff = staffList.find((item) => item.id === targetId);
    if (!staff) return;
    assignStudents.forEach((studentId) => {
      const student = students.find((item) => item.id === studentId);
      if (!student) return;
      controller.updateStudent(student.id, {
        keyWorker: { id: staff.id, name: staff.name, notes: '', goals: [] }
      });
    });
    setAssignModalOpen(false);
    setAssignStudents([]);
  };

  const applyReassignWorkload = () => {
    const target = staffList.find((item) => item.id === reassignTarget);
    if (!target) return;
    const selectedStaffIds = new Set(selectedIds);
    students.forEach((student) => {
      if (student.keyWorker?.id && selectedStaffIds.has(student.keyWorker.id)) {
        controller.updateStudent(student.id, {
          keyWorker: { id: target.id, name: target.name, notes: '', goals: [] }
        });
      }
    });
    setReassignModalOpen(false);
    setReassignTarget('');
  };

  return (
    <div className="space-y-6">
      <div className="sp-page-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Support Workers Workspace</h2>
          <p className="sp-muted">Track workload, accountability, and safeguarding oversight.</p>
        </div>
        <div className="sp-page-actions">
          <Button size="sm" onClick={() => navigate('/support-workers/new')}>Add staff</Button>
        </div>
      </div>
      {flashMessage && (
        <div className="sp-alert sp-alert--success text-sm flex items-center justify-between gap-3">
          <span>{flashMessage}</span>
          <Button size="sm" variant="ghost" onClick={() => setFlashMessage('')}>
            Dismiss
          </Button>
        </div>
      )}

      <WorkspaceKpis items={kpiItems} activeKpi={activeKpi} onSelect={setActiveKpi} />

      <div className="sp-filter-bar">
        <div className="sp-filter-main">
          <div>
            <label className="sp-label">Search</label>
            <div className="sp-search mt-2">
              <Icons.Search />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search staff"
              />
            </div>
          </div>
          <div className="sp-filter-controls">
            <div>
              <label className="sp-label">Programme</label>
              <select className="sp-select mt-2" disabled>
                <option>All programmes</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="sp-workspace-grid">
        <div>
          <div className="sp-table sp-table--staff-workspace">
            <div className="sp-row sp-row--header">
              <div>
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === filteredRows.length}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                  aria-label="Select all staff"
                />
              </div>
              <div>Name</div>
              <div>Role</div>
              <div>Active students</div>
              <div>Open incidents</div>
              <div>Overdue tasks</div>
              <div>Actions</div>
            </div>
            {filteredRows.map((row) => (
              <div
                key={row.id}
                className={`sp-row sp-row--inventory ${row.overdueTasks > 0 ? 'sp-row--warning' : ''}`}
                onClick={() => openDrawer(row.id)}
              >
                <div onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(row.id)}
                    onChange={() => toggleSelection(row.id)}
                    aria-label={`Select ${row.name}`}
                  />
                </div>
                <div>
                  <div className="sp-value" style={{ fontWeight: 600 }}>{row.name}</div>
                  <div className="text-xs sp-muted">{row.email || 'No email'}</div>
                </div>
                <div>{row.role}</div>
                <div>{row.assignedStudents.length}</div>
                <div>{row.openIncidents}</div>
                <div>
                  <Badge variant={row.overdueTasks > 0 ? 'warning' : 'active'}>
                    {row.overdueTasks > 0 ? `${row.overdueTasks} overdue` : 'On track'}
                  </Badge>
                </div>
                <div>
                  <Button size="sm" variant="ghost" onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/support-workers/${row.id}`);
                  }}>
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Card className={`sp-workspace-drawer ${drawerOpen ? 'is-open' : ''}`}>
          {!activeStaff ? (
            <div className="sp-drawer-empty">Select a staff member to view details.</div>
          ) : (
            <div className="space-y-4">
              <div className="sp-drawer-header">
                <div>
                  <div className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>{activeStaff.name}</div>
                  <div className="text-xs sp-muted">{activeStaff.role}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={closeDrawer}>Close</Button>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-drawer-meta">
                  <div>
                    <div className="sp-label">Assigned students</div>
                    <div className="sp-value">{activeStaff.assignedStudents.length}</div>
                  </div>
                  <div>
                    <div className="sp-label">Open incidents</div>
                    <div className="sp-value">{activeStaff.openIncidents}</div>
                  </div>
                  <div>
                    <div className="sp-label">Compliance issues</div>
                    <div className="sp-value">{activeStaff.compliance}</div>
                  </div>
                </div>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Assigned students</div>
                <div className="sp-drawer-timeline">
                  {activeStaff.assignedStudents.slice(0, 5).map((student) => (
                    <div key={student.id} className="sp-drawer-timeline-item">
                      <span>{student.name}</span>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/students/${student.id}/profile`)}>
                        View
                      </Button>
                    </div>
                  ))}
                  {activeStaff.assignedStudents.length === 0 && (
                    <div className="sp-drawer-empty">No assigned students.</div>
                  )}
                </div>
              </div>
              <div className="sp-drawer-actions">
                <Button size="sm" onClick={() => navigate(`/support-workers/${activeStaff.id}`)}>Open profile</Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSelectedIds(new Set([activeStaff.id]));
                    setAssignModalOpen(true);
                  }}
                >
                  Assign students
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {drawerOpen && <div className="sp-drawer-overlay" onClick={closeDrawer} aria-hidden="true" />}

      <BulkActionBar selectedCount={selectedIds.size} actions={bulkActions} />

      <Modal isOpen={assignModalOpen} onClose={() => setAssignModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Assign students</h3>
              <p className="text-sm sp-muted">Assign selected students to this support worker.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAssignModalOpen(false)}>Close</Button>
          </div>
          <SearchableChecklist
            items={students}
            selectedIds={assignStudents}
            onToggle={(id) => setAssignStudents((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id])}
            placeholder="Search students"
            getLabel={(student) => student.name}
            getMeta={(student) => student.reference || student.id}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            <Button onClick={applyAssignStudents} disabled={assignStudents.length === 0}>Assign</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={reassignModalOpen} onClose={() => setReassignModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Reassign workload</h3>
              <p className="text-sm sp-muted">Move selected staff caseload to another worker.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setReassignModalOpen(false)}>Close</Button>
          </div>
          <SearchableSelect
            value={reassignTarget}
            options={staffList.map((staff) => ({ value: staff.id, label: staff.name, meta: staff.role || '' }))}
            placeholder="Select new owner"
            emptyLabel="No staff found."
            onChange={setReassignTarget}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setReassignModalOpen(false)}>Cancel</Button>
            <Button onClick={applyReassignWorkload} disabled={!reassignTarget}>Reassign</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
